import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import WorkCard from "@/components/feature/WorkCard";
import Reveal from "@/components/base/Reveal";
import EmptyState from "@/components/base/EmptyState";
import { works as worksData } from "@/mocks/works";
import type { Work } from "@/types/content";

const works = worksData as Work[];

export default function WorkDetail() {
  const { id } = useParams();
  const work = works.find((w) => w.id === id);

  if (!work) {
    return (
      <div className="w-full min-h-screen bg-background-50">
        <title>作品不存在 - 奥田咲影视作品</title>
        <SiteHeader />
        <main className="w-full px-4 md:px-8 pt-28 md:pt-36 pb-20">
          <div className="w-full max-w-3xl mx-auto rounded-lg border border-background-200 bg-background-50">
            <EmptyState
              icon="ri-movie-line"
              title="抱歉，作品不存在或已下架"
              description="该项目可能已被移除，或者链接输入有误。你可以返回作品列表页，继续浏览奥田咲的其他影视作品。"
              actionLabel="返回作品列表"
              to="/works"
            />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = works
    .filter((w) => w.id !== work.id)
    .sort((a, b) => {
      const aScore = (a.type === work.type ? 2 : 0) + (Math.abs(a.year - work.year) <= 2 ? 1 : 0);
      const bScore = (b.type === work.type ? 2 : 0) + (Math.abs(b.year - work.year) <= 2 ? 1 : 0);
      return bScore - aScore || b.year - a.year;
    })
    .slice(0, 6);

  const metaItems = [
    { icon: "ri-price-tag-3-line", label: "作品类型", value: work.type },
    { icon: "ri-calendar-line", label: "上映年份", value: `${work.year} 年` },
    { icon: "ri-movie-2-line", label: "导演", value: work.director },
    { icon: "ri-user-star-line", label: "饰演角色", value: work.role },
    { icon: "ri-time-line", label: "片长", value: work.duration },
    { icon: "ri-earth-line", label: "制片地区", value: work.region },
  ];

  return (
    <div className="w-full min-h-screen bg-background-50">
      <title>{`${work.title} - 奥田咲影视资料详解`}</title>
      <meta
        name="description"
        content={`《${work.title}》是奥田咲于 ${work.year} 年出演的${work.type}，由${work.director}执导，奥田咲${work.role}。查看作品简介、演职信息与相关推荐。`}
      />
      <meta name="keywords" content={`${work.title},奥田咲,${work.type},奥田咲作品`} />

      <SiteHeader />

      <section className="w-full bg-primary-900 pt-24 md:pt-32 pb-10 md:pb-14">
        <div className="w-full px-4 md:px-8">
          <div className="w-full max-w-7xl mx-auto">
            <nav className="flex items-center flex-wrap gap-2 text-xs md:text-sm text-background-400">
              <Link to="/" className="hover:text-accent-300 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-background-500"></i>
              <Link to="/works" className="hover:text-accent-300 transition-colors">
                影视作品
              </Link>
              <i className="ri-arrow-right-s-line text-background-500"></i>
              <span className="text-background-200">{work.title}</span>
            </nav>
          </div>
        </div>
      </section>

      <main className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="w-full max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-4">
              <Reveal>
                <div className="relative w-full max-w-sm mx-auto lg:mx-0 aspect-[3/4] rounded-lg overflow-hidden bg-background-200 border border-background-200">
                  <img
                    src={work.cover}
                    alt={`${work.title} - 奥田咲影视作品封面`}
                    title={`${work.title} 奥田咲`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </Reveal>
              <Reveal className="mt-5 max-w-sm mx-auto lg:mx-0" delay={100}>
                <div className="flex items-center justify-between p-4 rounded-lg bg-background-100 border border-background-200">
                  <div className="flex items-center gap-2">
                    <i className="ri-star-fill text-accent-500 text-xl"></i>
                    <span className="font-heading text-2xl font-bold text-foreground-950">
                      {work.rating.toFixed(1)}
                    </span>
                  </div>
                  <span className="text-xs text-foreground-500">影迷综合评分</span>
                </div>
                <Link
                  to="/works"
                  className="mt-3 w-full flex items-center justify-center gap-2 px-5 py-3 rounded-md border border-background-300 text-sm font-medium text-foreground-800 hover:border-primary-400 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回作品列表
                </Link>
              </Reveal>
            </div>

            <div className="lg:col-span-8">
              <Reveal>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-3 py-1 rounded-md bg-accent-100 text-accent-800 text-xs font-semibold">
                    {work.type}
                  </span>
                  {work.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="mt-4 font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
                  {work.title}
                </h1>
                <p className="mt-3 text-sm md:text-base text-foreground-500">
                  {work.year} · {work.region} · {work.duration}
                </p>
              </Reveal>

              <Reveal delay={80}>
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {metaItems.map((m) => (
                    <div
                      key={m.label}
                      className="flex items-center gap-3 p-4 rounded-lg bg-background-100 border border-background-200"
                    >
                      <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 shrink-0">
                        <i className={`${m.icon} text-lg`}></i>
                      </span>
                      <div className="min-w-0">
                        <p className="text-xs text-foreground-500">{m.label}</p>
                        <p className="mt-0.5 text-sm font-medium text-foreground-900 break-words">
                          {m.value}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>

              <Reveal delay={140}>
                <div className="mt-10">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2">
                    <span className="w-1 h-6 rounded-full bg-accent-500"></span>
                    作品简介
                  </h2>
                  <p className="mt-5 text-sm md:text-base text-foreground-700 leading-9">
                    {work.synopsis}
                  </p>
                </div>
              </Reveal>
            </div>
          </div>

          <div className="mt-16 md:mt-20">
            <div className="flex items-end justify-between gap-4 mb-8">
              <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950">
                相关作品推荐
              </h2>
              <Link
                to="/works"
                className="text-sm text-primary-700 hover:text-accent-600 transition-colors whitespace-nowrap"
              >
                全部作品
                <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {related.map((item, idx) => (
                <Reveal key={item.id} delay={idx * 50}>
                  <WorkCard work={item} />
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}